/**
 * A listing's variants on the storefront (MERCH-87, ADR 0009 §3), pure. The server sends each
 * public listing with `options` ([{ name, key, style, values: [{ value, available, swatch? }] }]),
 * `variants` (active ones: [{ id, option_values, label, price_paise, mrp_paise, media_id, image_url,
 * available, unavailable_reason, qty_available, stock_mode }]) and `price_min_paise` /
 * `price_max_paise` (lib/catalog/variantView). Every listing has at least one variant; one with no
 * options has exactly one (its id is null until the store's mp-29 backfill ran).
 *
 * Availability is the server's: a combination it does not offer, or one it marks unavailable,
 * cannot be chosen into the cart here, and the server refuses it anyway (cart.addToCart).
 */
const NONE = Object.freeze([]);

export const optionsOf = (l) => (l && Array.isArray(l.options) ? l.options : NONE);
export const variantsOf = (l) => (l && Array.isArray(l.variants) ? l.variants : NONE);
/** The listing has choices to make (sizes, colours…). */
export const hasOptions = (l) => optionsOf(l).length > 0;

const valuesOf = (v) => (v && v.option_values) || {};
const matchesAll = (v, sel, options) => options.every((o) => sel[o.name] != null && valuesOf(v)[o.name] === sel[o.name]);

/** The variant the selection names, or null while it is incomplete or no variant has it. */
export function findVariant(l, sel) {
  const options = optionsOf(l);
  if (!options.length) return variantsOf(l)[0] || null;
  return variantsOf(l).find((v) => matchesAll(v, sel || {}, options)) || null;
}

/** A first selection: the first variant a shopper can buy, else the first one. */
export function initialSelection(l) {
  const list = variantsOf(l);
  const first = list.find((v) => v.available) || list[0];
  return first ? { ...valuesOf(first) } : {};
}

/**
 * The state of one value given the rest of the selection: `offered` when some variant has it
 * with the other chosen values, `available` when such a variant can be bought now.
 */
export function valueState(l, sel, name, value) {
  const options = optionsOf(l);
  const want = { ...(sel || {}), [name]: value };
  const same = variantsOf(l).filter((v) => options.every((o) => o.name === name ? valuesOf(v)[o.name] === value : want[o.name] == null || valuesOf(v)[o.name] === want[o.name]));
  return { offered: same.length > 0, available: same.some((v) => v.available) };
}

/**
 * Choose `value` for option `name`. When no variant has it with the other chosen values, the
 * nearest one that has it (an available one first) supplies them, so a choice always lands on
 * a real variant.
 */
export function choose(l, sel, name, value) {
  const next = { ...(sel || {}), [name]: value };
  if (findVariant(l, next)) return next;
  const options = optionsOf(l);
  const score = (v) => options.filter((o) => valuesOf(v)[o.name] === next[o.name]).length + (v.available ? options.length : 0);
  const candidates = variantsOf(l).filter((v) => valuesOf(v)[name] === value).sort((a, b) => score(b) - score(a));
  return candidates.length ? { ...valuesOf(candidates[0]) } : next;
}

/** The listing as the chosen variant sells it: its price, MRP and stock; `variant_id` for the cart. */
export function asVariant(l, v) {
  if (!l || !v) return l;
  return {
    ...l,
    variant_id: v.id,
    variant_label: v.label || null,
    price_paise: v.price_paise != null ? v.price_paise : l.price_paise,
    mrp_paise: v.mrp_paise != null ? v.mrp_paise : l.mrp_paise,
    stock_mode: v.stock_mode || l.stock_mode,
    qty_available: v.qty_available,
    available: v.available,
  };
}

/** Why the chosen combination cannot go in the cart (null when it can). */
export function unavailableText(l, sel) {
  if (!hasOptions(l)) return null;
  const missing = optionsOf(l).find((o) => sel == null || sel[o.name] == null);
  if (missing) return `Choose a ${missing.name.toLowerCase()}`;
  const v = findVariant(l, sel);
  if (!v) return 'This combination is not available';
  if (!v.available) return v.unavailable_reason === 'out of stock' ? 'Out of stock' : 'Not available right now';
  return null;
}

/** { min, max, varies }: the price range on a card (`varies` when variants differ in price). */
export function priceRange(l) {
  const min = l && l.price_min_paise != null ? Number(l.price_min_paise) : Number((l && l.price_paise) || 0);
  const max = l && l.price_max_paise != null ? Number(l.price_max_paise) : min;
  return { min, max: Math.max(min, max), varies: max > min };
}

/** The colour swatches a card shows: the first swatch-style option's values, with their colour. */
export function cardSwatches(l, limit = 5) {
  const o = optionsOf(l).find((x) => x.style === 'swatch');
  if (!o) return { values: NONE, more: 0, name: null };
  const values = o.values.filter((x) => x.available !== false);
  return { name: o.name, values: values.slice(0, limit), more: Math.max(0, values.length - limit) };
}

/** The gallery position of a variant's image (its media_id), or -1. */
export const galleryIndexOf = (gallery, mediaId) => (mediaId == null ? -1 : (gallery || []).findIndex((g) => g && g.media_id != null && Number(g.media_id) === Number(mediaId)));

/** A cart line's identity: the listing and, for a listing with options, its variant. */
export const lineKey = (listingId, variantId) => `${listingId}:${variantId == null ? '' : variantId}`;
